import React, {
	Children,
	cloneElement,
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import { debounce, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import {
	createGoogleMap,
	getDistance,
	locationsToBounds,
} from 'utils/search/autocomplete';

const GoogleMap = ({
	children,
	radius,
	center,
	locationBounds,
	onLoad,
	onMove,
	searchFromMove,
	setSearchFromMove,
}) => {
	const [map, setMap] = useState(null);
	const [mapZoom, setMapZoom] = useState(null);
	const [google, setGoogle] = useState(null);

	const instanceRef = useRef(null);
	const mapRef = useRef(null);
	const googleRef = useRef(null);
	const mapZoomRef = useRef(null);
	const prevCenterRef = useRef(center);

	// Keep refs in sync
	mapRef.current = map;
	googleRef.current = google;
	mapZoomRef.current = mapZoom;

	const setBounds = useCallback(() => {
		const g = googleRef.current;
		const m = mapRef.current;
		if (!g || !m) return;
		const bounds = locationsToBounds(g, [center, ...locationBounds]);

		if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
			const extendPoint1 = new g.maps.LatLng(
				bounds.getNorthEast().lat() + 0.001,
				bounds.getNorthEast().lng() + 0.001,
			);
			const extendPoint2 = new g.maps.LatLng(
				bounds.getNorthEast().lat() - 0.001,
				bounds.getNorthEast().lng() - 0.001,
			);
			bounds.extend(extendPoint1);
			bounds.extend(extendPoint2);
		}

		m.fitBounds(bounds, 0);
	}, [center, locationBounds]);

	const onMapMove = useCallback(() => {
		const m = mapRef.current;
		if (!m) return;
		const mapCenter = m.getCenter();
		const zoom = m.getZoom();
		const dX = getDistance(
			mapCenter.lat(),
			mapCenter.lng(),
			mapCenter.lat(),
			center.longitude,
		);
		const dY = getDistance(
			mapCenter.lat(),
			mapCenter.lng(),
			center.latitude,
			mapCenter.lng(),
		);

		if (onMove && (dX > 2 || dY > 2 || mapZoomRef.current !== zoom)) {
			onMove({
				latitude: mapCenter.lat(),
				longitude: mapCenter.lng(),
			});
			setMapZoom(zoom);
		}
	}, [center, onMove]);

	// Mount: create the google map
	useEffect(() => {
		const options = {
			radius: radius || 15,
			center,
			onDragEnd: debounce(onMapMove, 400),
		};

		createGoogleMap(instanceRef.current, options).then(
			({ google: g, map: m }) => {
				setGoogle(g);
				setMap(m);
				googleRef.current = g;
				mapRef.current = m;

				if (locationBounds) {
					const bounds = locationsToBounds(g, [
						center,
						...locationBounds,
					]);
					if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
						const extendPoint1 = new g.maps.LatLng(
							bounds.getNorthEast().lat() + 0.001,
							bounds.getNorthEast().lng() + 0.001,
						);
						const extendPoint2 = new g.maps.LatLng(
							bounds.getNorthEast().lat() - 0.001,
							bounds.getNorthEast().lng() - 0.001,
						);
						bounds.extend(extendPoint1);
						bounds.extend(extendPoint2);
					}
					m.fitBounds(bounds, 0);
				}

				setMapZoom(m.getZoom());

				if (onLoad) {
					onLoad(true);
				}
			},
		);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle center/bounds changes
	useEffect(() => {
		if (!mapRef.current || !googleRef.current) return;

		const isSearchFromMoveFlag = searchFromMove || false;
		const doNotMoveTheMap = isSearchFromMoveFlag;

		if (center && !isEqual(center, prevCenterRef.current)) {
			if (locationBounds && !doNotMoveTheMap) {
				setBounds();
			} else {
				mapRef.current.setCenter(
					new googleRef.current.maps.LatLng(
						center.latitude,
						center.longitude,
					),
				);
			}

			if (isSearchFromMoveFlag && setSearchFromMove) {
				setSearchFromMove(false);
			}
		}
		prevCenterRef.current = center;
	}, [center, locationBounds, searchFromMove, setSearchFromMove, setBounds]);

	return (
		<div className="google-map-container">
			<div
				className="google-map"
				ref={instanceRef}
			>
				{!!map &&
					Children.toArray(children).map((child) =>
						cloneElement(child, { google, map }),
					)}
			</div>
		</div>
	);
};

GoogleMap.propTypes = {
	children: PropTypes.node,
	radius: PropTypes.number,
	center: PropTypes.shape({
		latitude: PropTypes.number,
		longitude: PropTypes.number,
	}),
	locationBounds: PropTypes.arrayOf(
		PropTypes.shape({
			latitude: PropTypes.number,
			longitude: PropTypes.number,
		}),
	),
	onLoad: PropTypes.func,
	onMove: PropTypes.func,
	searchFromMove: PropTypes.bool,
	setSearchFromMove: PropTypes.func,
};

export default GoogleMap;
